import React, { useEffect, useMemo, useState } from 'react';
import { ResponsiveContainer, ResponsiveContent, ResponsiveHeader } from "../ui";
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import Modal from '../ui/Modal';
import FileUpload from '../ui/FileUpload';
import { documentService } from '../../services/documentService';
import type { AppDocument } from '../../types/document';
import {
  NewDogIcon,
  NewCatIcon,
  NewBirdIcon,
  NewFishIcon,
  NewReptilesIcon,
  NewOtherPetsIcon,
  NewChildIcon,
  NewParentsIcon,
  NewSiblingsIcon,
  NewSpousesIcon,
  NewOtherDependentsIcon,
} from '../ui/Icons';

interface Pet {
  id: string;
  name: string;
  type: 'dog' | 'cat' | 'bird' | 'fish' | 'reptile' | 'other' | '';
  breed: string;
  age: string;
  specialNeeds: string;
  veterinarian: string;
  veterinarianContact: string;
  preferredCaregiver: string;
  caregiverContact: string;
  careInstructions: string;
  diet: string;
  medications: string;
  emergencyContact: string;
  emergencyContactPhone: string;
  documents: AppDocument[];
}

interface Dependant {
  id: string;
  name: string;
  relationship: 'child' | 'parent' | 'sibling' | 'spouse' | 'other' | '';
  age: string;
  specialNeeds: string;
  medicalConditions: string;
  medications: string;
  preferredGuardian: string;
  guardianContact: string;
  emergencyContact: string;
  emergencyContactPhone: string;
  careInstructions: string;
  documents: AppDocument[];
}

interface PetsAndDependantsData {
  pets: Pet[];
  dependants: Dependant[];
}

const petCategories = [
  { key: 'dog' as const, label: 'Dogs', Icon: NewDogIcon, description: 'Add your dogs and their care instructions' },
  { key: 'cat' as const, label: 'Cats', Icon: NewCatIcon, description: 'Add your cats and their care instructions' },
  { key: 'bird' as const, label: 'Birds', Icon: NewBirdIcon, description: 'Add your birds and their care instructions' },
  { key: 'fish' as const, label: 'Fish', Icon: NewFishIcon, description: 'Add your fish and their care instructions' },
  { key: 'reptile' as const, label: 'Reptiles', Icon: NewReptilesIcon, description: 'Add your reptiles and their care instructions' },
  { key: 'other' as const, label: 'Other Pets', Icon: NewOtherPetsIcon, description: 'Add any other pets not listed above' },
];

const dependentCategories = [
  { key: 'child' as const, label: 'Children', Icon: NewChildIcon, description: 'Add your dependent children and their care needs' },
  { key: 'parent' as const, label: 'Parents', Icon: NewParentsIcon, description: 'Add your dependent parents and their care needs' },
  { key: 'sibling' as const, label: 'Siblings', Icon: NewSiblingsIcon, description: 'Add your dependent siblings and their care needs' },
  { key: 'spouse' as const, label: 'Spouse', Icon: NewSpousesIcon, description: 'Add your spouse if they require special care' },
  { key: 'other' as const, label: 'Other Dependents', Icon: NewOtherDependentsIcon, description: 'Add any other dependent family members' },
];

/* Summary card icon for adult dependents */
const AdultDependentsIcon = ({ className = 'w-6 h-6 text-cyan-700' }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M16 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle
      cx="9.5"
      cy="7"
      r="4"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M20 21v-2a4 4 0 0 0-3-3.87"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M16 3.13a4 4 0 0 1 0 7.75"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/* Only keep valid backend-backed documents */
const normalizeDocuments = (docs: unknown): AppDocument[] => {
  if (!Array.isArray(docs)) return [];

  return docs
    .filter((doc): doc is Record<string, unknown> => !!doc && typeof doc === 'object')
    .map((doc) => ({
      id: typeof doc.id === 'string' ? doc.id : '',
      name: typeof doc.name === 'string' ? doc.name : 'Untitled document',
      mimeType: typeof doc.mimeType === 'string' ? doc.mimeType : undefined,
      size: typeof doc.size === 'number' ? doc.size : undefined,
      createdAt: typeof doc.createdAt === 'string' ? doc.createdAt : undefined,
    }))
    .filter((doc) => !!doc.id);
};

const normalizeData = (raw: unknown): PetsAndDependantsData => {
  const safe = (raw && typeof raw === 'object') ? (raw as Partial<PetsAndDependantsData>) : {};

  const pets = Array.isArray(safe.pets) ? safe.pets.map((pet) => ({
    ...(pet as Pet),
    documents: normalizeDocuments((pet as Partial<Pet>).documents),
  })) : [];

  const dependants = Array.isArray(safe.dependants) ? safe.dependants.map((dependant) => ({
    ...(dependant as Dependant),
    documents: normalizeDocuments((dependant as Partial<Dependant>).documents),
  })) : [];

  return { pets, dependants };
};

/* Read saved pets/dependants backup when session data is missing */
const loadFromLocalStorage = (): PetsAndDependantsData => {
  try {
    const backupData = localStorage.getItem('pets-dependants-backup');
    if (!backupData) {
      return { pets: [], dependants: [] };
    }

    const parsed = JSON.parse(backupData);
    return normalizeData(parsed);
  } catch (error) {
    console.error('[Frame146] Failed to load pets/dependants backup:', error);
    return { pets: [], dependants: [] };
  }
};

/* Save pets/dependants into the same backup keys used by the entry pages */
const saveToLocalStorage = (dataToSave: PetsAndDependantsData) => {
  try {
    const saveData = {
      answers: {
        petsAndDependants: dataToSave,
      },
      lastUpdated: new Date().toISOString(),
    };

    localStorage.setItem('pets-dependants-backup', JSON.stringify(dataToSave));
    localStorage.setItem('pets-dependants-data-backup', JSON.stringify(saveData));
    return true;
  } catch (error) {
    console.error('[Frame146] Direct save failed:', error);
    return false;
  }
};

/* Match the same pet icons used on the add-entry frame */
const getPetIcon = (type: Pet['type'], className = 'w-6 h-6 text-cyan-700') => {
  const match = petCategories.find((category) => category.key === type);
  const Icon = match?.Icon || NewOtherPetsIcon;
  return <Icon className={className} />;
};

/* Match the same dependent icons used on the add-entry frame */
const getDependentIcon = (
  relationship: Dependant['relationship'],
  className = 'w-6 h-6 text-cyan-700'
) => {
  const match = dependentCategories.find((category) => category.key === relationship);
  const Icon = match?.Icon || NewOtherDependentsIcon;
  return <Icon className={className} />;
};

const getDocumentIcon = (doc: AppDocument, className = 'w-4 h-4 text-cyan-700') => {
  const fileName = doc.name.toLowerCase();
  const fileType = (doc.mimeType || '').toLowerCase();

  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14 3v5h5"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {fileType.includes('pdf') || fileName.endsWith('.pdf') ? (
        <path
          d="M8 13h8M8 17h6"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ) : (
        <path
          d="M8 13h8M8 17h8"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
    </svg>
  );
};

const openDocument = (doc: AppDocument) => {
  if (!doc.id) return;
  documentService.open(doc.id);
};

const downloadDocument = async (doc: AppDocument) => {
  if (!doc.id) return;

  try {
    await documentService.download(doc.id, doc.name);
  } catch (error) {
    console.error('[Frame146] Failed to download document:', error);
    alert('Failed to download document.');
  }
};

const Frame146: React.FC = () => {
  const { session, updateTaskProgress } = useSessionPersistence();

  const [data, setData] = useState<PetsAndDependantsData>({ pets: [], dependants: [] });
  const [loading, setLoading] = useState(true);

  /* Entry chooser modal lets the user pick pet vs dependent type first */
  const [showChooserModal, setShowChooserModal] = useState(false);

  /* Shared add/edit form modal state */
  const [showFormModal, setShowFormModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<'pet' | 'dependent'>('pet');
  const [selectedType, setSelectedType] = useState<Pet['type'] | Dependant['relationship']>('');
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [formTitle, setFormTitle] = useState('');

  /* Row expansion state so cards stay compact until opened */
  const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>({});

  /* Shared form fields */
  const [name, setName] = useState('');
  const [age, setAge] = useState('');
  const [specialNeeds, setSpecialNeeds] = useState('');
  const [primaryContact, setPrimaryContact] = useState('');
  const [primaryContactPhone, setPrimaryContactPhone] = useState('');
  const [secondaryContact, setSecondaryContact] = useState('');
  const [secondaryContactPhone, setSecondaryContactPhone] = useState('');
  const [medicalInfo, setMedicalInfo] = useState('');
  const [medications, setMedications] = useState('');
  const [careInstructions, setCareInstructions] = useState('');
  const [diet, setDiet] = useState('');
  const [petMedications, setPetMedications] = useState('');
  const [documents, setDocuments] = useState<AppDocument[]>([]);
  const [isUploadingDocuments, setIsUploadingDocuments] = useState(false);

  /* Load pets/dependants from session first, then fallback to local backup */
  useEffect(() => {
    if (session?.taskProgress?.pets?.answers?.petsAndDependants) {
      const savedData = session.taskProgress.pets.answers.petsAndDependants;
      setData(normalizeData(savedData));
      setLoading(false);
      return;
    }

    setData(loadFromLocalStorage());
    setLoading(false);
  }, [session?.taskProgress?.pets]);

  /* Split dependants into children and adult dependents for summary cards and sections */
  const children = useMemo(
    () => data.dependants.filter((dependant) => dependant.relationship === 'child'),
    [data.dependants]
  );

  const adultDependants = useMemo(
    () => data.dependants.filter((dependant) => dependant.relationship && dependant.relationship !== 'child'),
    [data.dependants]
  );

  /* Summary cards shown above the overview list */
  const statCards = [
    {
      label: 'Children',
      value: children.length,
      icon: <NewChildIcon className="w-6 h-6 text-cyan-700" />,
    },
    {
      label: 'Adult Dependents',
      value: adultDependants.length,
      icon: <AdultDependentsIcon className="w-6 h-6 text-cyan-700" />,
    },
    {
      label: 'Pets',
      value: data.pets.length,
      icon: <NewDogIcon className="w-6 h-6 text-cyan-700" />,
    },
  ];

  /* Save to React state, local backup, and session taskProgress */
  const persistData = async (newData: PetsAndDependantsData) => {
    setData(newData);
    saveToLocalStorage(newData);

    try {
      await updateTaskProgress('pets', { petsAndDependants: newData });
    } catch (error) {
      console.error('[Frame146] Session save failed:', error);
    }
  };

  /* Reset shared modal fields before create/edit flows */
  const resetForm = () => {
    setEditingItemId(null);
    setName('');
    setAge('');
    setSpecialNeeds('');
    setPrimaryContact('');
    setPrimaryContactPhone('');
    setSecondaryContact('');
    setSecondaryContactPhone('');
    setMedicalInfo('');
    setMedications('');
    setCareInstructions('');
    setDiet('');
    setPetMedications('');
    setDocuments([]);
    setIsUploadingDocuments(false);
  };

  /* Toggle a summary card between collapsed and expanded view */
  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  /* Open the chooser for a brand-new entry */
  const handleOpenAddChooser = () => {
    resetForm();
    setShowChooserModal(true);
  };

  /* Open the shared form in pet mode after type selection */
  const openPetForm = (type: Pet['type']) => {
    resetForm();
    setSelectedCategory('pet');
    setSelectedType(type);
    setFormTitle(`Add ${type.charAt(0).toUpperCase() + type.slice(1)}`);
    setShowChooserModal(false);
    setShowFormModal(true);
  };

  /* Open the shared form in dependent mode after relationship selection */
  const openDependentForm = (relationship: Dependant['relationship']) => {
    resetForm();
    setSelectedCategory('dependent');
    setSelectedType(relationship);
    setFormTitle(`Add ${relationship.charAt(0).toUpperCase() + relationship.slice(1)}`);
    setShowChooserModal(false);
    setShowFormModal(true);
  };

  /* Load an existing pet or dependent into the shared form for editing */
  const handleEdit = (item: Pet | Dependant, category: 'pet' | 'dependent') => {
    resetForm();
    setSelectedCategory(category);
    setSelectedType(category === 'pet' ? (item as Pet).type : (item as Dependant).relationship);
    setEditingItemId(item.id);
    setName(item.name);
    setAge(item.age);
    setSpecialNeeds(item.specialNeeds);

    if (category === 'pet') {
      const pet = item as Pet;
      setPrimaryContact(pet.veterinarian);
      setPrimaryContactPhone(pet.veterinarianContact);
      setSecondaryContact(pet.preferredCaregiver);
      setSecondaryContactPhone(pet.caregiverContact);
      setCareInstructions(pet.careInstructions);
      setDiet(pet.diet || '');
      setPetMedications(pet.medications || '');
    } else {
      const dependant = item as Dependant;
      setPrimaryContact(dependant.preferredGuardian);
      setPrimaryContactPhone(dependant.guardianContact);
      setSecondaryContact(dependant.emergencyContact);
      setSecondaryContactPhone(dependant.emergencyContactPhone);
      setMedicalInfo(dependant.medicalConditions);
      setMedications(dependant.medications);
      setCareInstructions(dependant.careInstructions);
    }

    setDocuments(normalizeDocuments(item.documents || []));
    setFormTitle(`Edit ${item.name}`);
    setShowFormModal(true);
  };

  /* Delete a pet or dependent, then persist the updated list */
  const handleDelete = async (id: string, category: 'pet' | 'dependent') => {
    if (!confirm('Are you sure you want to delete this entry?')) return;

    const sourceItem =
      category === 'pet'
        ? data.pets.find((pet) => pet.id === id)
        : data.dependants.find((dependant) => dependant.id === id);

    const itemDocuments = sourceItem?.documents || [];

    for (const doc of itemDocuments) {
      try {
        await documentService.delete(doc.id);
      } catch (error) {
        console.error('[Frame146] Failed to delete document while deleting entry:', error);
      }
    }

    if (category === 'pet') {
      await persistData({
        ...data,
        pets: data.pets.filter((pet) => pet.id !== id),
      });
    } else {
      await persistData({
        ...data,
        dependants: data.dependants.filter((dependant) => dependant.id !== id),
      });
    }
  };

  const removeDocumentFromForm = async (index: number) => {
    const docToRemove = documents[index];
    if (!docToRemove) return;

    try {
      await documentService.delete(docToRemove.id);
    } catch (error) {
      console.error('[Frame146] Failed to delete unsaved document:', error);
      alert('Failed to delete document.');
      return;
    }

    setDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemoveSavedDocument = async (
    category: 'pet' | 'dependent',
    itemId: string,
    documentIndex: number
  ) => {
    const sourceItem =
      category === 'pet'
        ? data.pets.find((pet) => pet.id === itemId)
        : data.dependants.find((dependant) => dependant.id === itemId);

    const docToRemove = sourceItem?.documents?.[documentIndex];
    if (!docToRemove) return;

    try {
      await documentService.delete(docToRemove.id);
    } catch (error) {
      console.error('[Frame146] Failed to delete document from backend:', error);
      alert('Failed to delete document.');
      return;
    }

    if (category === 'pet') {
      const updatedPets = data.pets.map((pet) =>
        pet.id === itemId
          ? {
              ...pet,
              documents: pet.documents.filter((_, index) => index !== documentIndex),
            }
          : pet
      );

      await persistData({
        ...data,
        pets: updatedPets,
      });
    } else {
      const updatedDependants = data.dependants.map((dependant) =>
        dependant.id === itemId
          ? {
              ...dependant,
              documents: dependant.documents.filter((_, index) => index !== documentIndex),
            }
          : dependant
      );

      await persistData({
        ...data,
        dependants: updatedDependants,
      });
    }
  };

  /* Validate and save the shared add/edit form */
  const handleSave = async () => {
    if (isUploadingDocuments) return;

    if (!name.trim()) {
      alert('Name is required');
      return;
    }

    if (selectedCategory === 'pet') {
      const newPet: Pet = {
        id: editingItemId || Date.now().toString(),
        name: name.trim(),
        type: selectedType as Pet['type'],
        breed: '',
        age: age.trim(),
        specialNeeds: specialNeeds.trim(),
        veterinarian: primaryContact.trim(),
        veterinarianContact: primaryContactPhone.trim(),
        preferredCaregiver: secondaryContact.trim(),
        caregiverContact: secondaryContactPhone.trim(),
        careInstructions: careInstructions.trim(),
        diet: diet.trim(),
        medications: petMedications.trim(),
        emergencyContact: '',
        emergencyContactPhone: '',
        documents: documents,
      };

      const updatedPets = editingItemId
        ? data.pets.map((pet) => (pet.id === editingItemId ? newPet : pet))
        : [...data.pets, newPet];

      await persistData({
        ...data,
        pets: updatedPets,
      });
    } else {
      const newDependant: Dependant = {
        id: editingItemId || Date.now().toString(),
        name: name.trim(),
        relationship: selectedType as Dependant['relationship'],
        age: age.trim(),
        specialNeeds: specialNeeds.trim(),
        medicalConditions: medicalInfo.trim(),
        medications: medications.trim(),
        preferredGuardian: primaryContact.trim(),
        guardianContact: primaryContactPhone.trim(),
        emergencyContact: secondaryContact.trim(),
        emergencyContactPhone: secondaryContactPhone.trim(),
        careInstructions: careInstructions.trim(),
        documents: documents,
      };

      const updatedDependants = editingItemId
        ? data.dependants.map((dependant) => (dependant.id === editingItemId ? newDependant : dependant))
        : [...data.dependants, newDependant];

      await persistData({
        ...data,
        dependants: updatedDependants,
      });
    }

    setShowFormModal(false);
    resetForm();
  };

  return (
    <ResponsiveContainer>
      <ResponsiveContent>
        <div className="w-full max-w-[835px] min-w-0 overflow-x-hidden">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 min-w-0">
            <div className="flex-1 min-w-0">
              <ResponsiveHeader title="Dependents and Pets" />
            </div>
            <div className="shrink-0 w-full sm:w-auto">
              <button
                onClick={handleOpenAddChooser}
                className="flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2 bg-cyan-700 text-white text-sm font-medium rounded-md hover:bg-cyan-800 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Entry
              </button>
            </div>
          </div>

          <p className="text-stone-800 text-lg font-medium font-['Work_Sans'] leading-relaxed break-words">
            Document your children, adult dependents, and pets along with guardianship arrangements and care instructions.
          </p>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-5 min-w-0">
            {statCards.map((card) => (
              <div
                key={card.label}
                className="bg-white rounded-md border border-stone-200 p-6 min-w-0 overflow-hidden"
              >
                <div className="flex items-start gap-3 min-w-0">
                  <div className="shrink-0">{card.icon}</div>

                  <div className="flex flex-col min-w-0">
                    <div className="font-sans text-base text-stone-600 break-words">
                      {card.label}
                    </div>

                    <div className="font-sans text-3xl font-medium text-stone-800 leading-none mt-4 break-words">
                      {loading ? '—' : card.value}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* ── Children ── */}
        <div className="mt-7 min-w-0">
          <div className="flex items-center gap-3 mb-3 min-w-0">
            <h2 className="font-sans text-xl font-medium text-stone-800 break-words">
              Children
            </h2>
          </div>

          <div className="space-y-3 min-w-0">
            {loading ? (
              <div className="text-center py-12 text-stone-500">
                <div className="inline-block w-6 h-6 border-2 border-cyan-700 border-t-transparent rounded-full animate-spin mb-2" />
                <p className="font-sans text-sm">Loading children...</p>
              </div>
            ) : children.length === 0 ? (
              <div className="text-center py-12 border border-dashed border-stone-300 rounded-lg">
                <p className="font-sans text-stone-500 text-sm">
                  No children added yet.
                </p>
              </div>
            ) : (
              children.map((child) => {
                const isExpanded = !!expandedIds[`child-${child.id}`];
                const metadataParts = [
                  child.age || null,
                  child.preferredGuardian ? `Guardian: ${child.preferredGuardian}` : null,
                ].filter(Boolean);

                return (
                  <div
                    key={child.id}
                    className="flex flex-col gap-4 bg-white rounded-lg border border-stone-200 p-4 sm:p-6 min-w-0 overflow-hidden"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start gap-4 min-w-0">
                      <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4 flex-1 min-w-0 overflow-hidden">
                        {getDependentIcon(child.relationship, 'w-6 h-6 text-cyan-700 flex-shrink-0 mt-0.5')}

                        <div className="flex-1 min-w-0 w-full overflow-hidden">
                          <div className="font-sans text-lg font-medium text-stone-800 break-words">
                            {child.name}
                          </div>

                          <div className="font-sans text-base text-stone-600 mt-1 break-words">
                            Child
                          </div>

                          {metadataParts.length > 0 && (
                            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-y-1 sm:gap-x-5 font-sans text-base text-stone-600 mt-1 min-w-0">
                              {metadataParts.map((part, index) => (
                                <span key={index} className="break-words min-w-0">
                                  {part}
                                </span>
                              ))}
                            </div>
                          )}

                          {child.specialNeeds && (
                            <div className="font-sans text-sm text-stone-500 mt-2 break-words leading-relaxed min-w-0">
                              {child.specialNeeds}
                            </div>
                          )}

                          {/* Document Section */}
                          {child.documents && child.documents.length > 0 && (
                            <div className="mt-3 space-y-2 min-w-0 overflow-hidden">
                                <div className="text-sm text-cyan-700 font-medium">
                                  {child.documents.length} document(s) attached
                                </div>
                              
                              <div className="space-y-2 min-w-0">
                                {child.documents.map((doc, index) => (
                                  <div
                                    key={`${child.id}-${doc.id}-${index}`}
                                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-md border border-stone-200 bg-stone-50 px-3 py-2 min-w-0"
                                  >
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openDocument(doc);
                                      }}
                                      className="flex items-start gap-2 min-w-0 w-full sm:flex-1 text-sm font-medium text-stone-800 hover:text-cyan-700 transition-colors text-left"
                                    >
                                      {getDocumentIcon(doc, 'w-4 h-4 text-cyan-700 shrink-0 mt-0.5')}
                                      <span className="break-words whitespace-normal min-w-0">
                                        {doc.name}
                                      </span>
                                    </button>

                                    <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          downloadDocument(doc);
                                        }}
                                        className="text-cyan-600 hover:text-cyan-800 text-xs font-medium"
                                        aria-label={`Download ${doc.name}`}
                                        title="Download document"
                                      >
                                        Download
                                      </button>

                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleRemoveSavedDocument('dependent', child.id, index);
                                        }}
                                        className="text-red-500 hover:text-red-700 text-xs font-medium"
                                        aria-label={`Remove ${doc.name}`}
                                        title="Remove document"
                                      >
                                        Remove
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {isExpanded && (
                            <div className="mt-3 space-y-2 min-w-0 overflow-hidden border-t border-stone-100 pt-3">
                              {child.guardianContact && (
                                <div className="font-sans text-sm text-stone-500 break-words leading-relaxed min-w-0">
                                  <span className="font-medium text-stone-700">Guardian Contact:</span> {child.guardianContact}
                                </div>
                              )}

                              {child.emergencyContact && (
                                <div className="font-sans text-sm text-stone-500 break-words leading-relaxed min-w-0">
                                  <span className="font-medium text-stone-700">Emergency Contact:</span> {child.emergencyContact}
                                </div>
                              )}

                              {child.emergencyContactPhone && (
                                <div className="font-sans text-sm text-stone-500 break-words leading-relaxed min-w-0">
                                  <span className="font-medium text-stone-700">Emergency Phone:</span> {child.emergencyContactPhone}
                                </div>
                              )}

                              {child.medicalConditions && (
                                <div className="font-sans text-sm text-stone-500 break-words leading-relaxed min-w-0">
                                  <span className="font-medium text-stone-700">Medical Conditions:</span> {child.medicalConditions}
                                </div>
                              )}

                              {child.medications && (
                                <div className="font-sans text-sm text-stone-500 break-words leading-relaxed min-w-0">
                                  <span className="font-medium text-stone-700">Medications:</span> {child.medications}
                                </div>
                              )}

                              {child.careInstructions && (
                                <div className="font-sans text-sm text-stone-500 whitespace-pre-wrap break-words leading-relaxed min-w-0">
                                  <span className="font-medium text-stone-700">Care Instructions:</span><br/>
                                  {child.careInstructions}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2 shrink-0 w-full sm:w-auto">
                        <button
                          onClick={() => toggleExpanded(`child-${child.id}`)}
                          className="text-stone-700 hover:text-stone-900 px-3 py-1 rounded border border-stone-300 hover:border-stone-500 font-sans text-sm font-medium transition-colors"
                        >
                          {isExpanded ? 'Show Less' : 'Show More'}
                        </button>
                        <button
                          onClick={() => handleEdit(child, 'dependent')}
                          className="text-cyan-600 hover:text-cyan-800 px-3 py-1 rounded border border-cyan-400 hover:border-cyan-700 font-sans text-sm font-medium transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(child.id, 'dependent')}
                          className="text-red-600 hover:text-red-800 px-3 py-1 rounded border border-red-300 hover:border-red-700 font-sans text-sm font-medium transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

          {/* ── Adult Dependents ── */}
        <div className="mt-7 min-w-0">
          <div className="flex items-center gap-3 mb-3 min-w-0">
            <h2 className="font-sans text-xl font-medium text-stone-800 break-words">
              Adult Dependents
            </h2>
          </div>

          <div className="space-y-3 min-w-0">
            {loading ? (
              <div className="text-center py-12 text-stone-500">
                <div className="inline-block w-6 h-6 border-2 border-cyan-700 border-t-transparent rounded-full animate-spin mb-2" />
                <p className="font-sans text-sm">Loading adult dependents...</p>
              </div>
            ) : adultDependants.length === 0 ? (
              <div className="text-center py-12 border border-dashed border-stone-300 rounded-lg">
                <p className="font-sans text-stone-500 text-sm">
                  No adult dependents added yet.
                </p>
              </div>
            ) : (
              adultDependants.map((dependant) => {
                const isExpanded = !!expandedIds[`adult-${dependant.id}`];
                const relationshipLabel =
                  dependant.relationship.charAt(0).toUpperCase() + dependant.relationship.slice(1);

                const metadataParts = [
                  dependant.age || null,
                  dependant.preferredGuardian ? `Guardian: ${dependant.preferredGuardian}` : null,
                ].filter(Boolean);

                return (
                  <div
                    key={dependant.id}
                    className="flex flex-col gap-4 bg-white rounded-lg border border-stone-200 p-4 sm:p-6 min-w-0 overflow-hidden"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start gap-4 min-w-0">
                      <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4 flex-1 min-w-0 overflow-hidden">
                        {getDependentIcon(dependant.relationship, 'w-6 h-6 text-cyan-700 flex-shrink-0 mt-0.5')}

                        <div className="flex-1 min-w-0 w-full overflow-hidden">
                          <div className="font-sans text-lg font-medium text-stone-800 break-words">
                            {dependant.name}
                          </div>

                          <div className="font-sans text-base text-stone-600 mt-1 break-words">
                            {relationshipLabel}
                          </div>

                          {metadataParts.length > 0 && (
                            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-y-1 sm:gap-x-5 font-sans text-base text-stone-600 mt-1 min-w-0">
                              {metadataParts.map((part, index) => (
                                <span key={index} className="break-words min-w-0">
                                  {part}
                                </span>
                              ))}
                            </div>
                          )}

                          {dependant.specialNeeds && (
                            <div className="font-sans text-sm text-stone-500 mt-2 break-words leading-relaxed min-w-0">
                              {dependant.specialNeeds}
                            </div>
                          )}

                          {/* Document Section */}
                          {dependant.documents && dependant.documents.length > 0 && (
                            <div className="mt-3 space-y-2 min-w-0 overflow-hidden">
                                <div className="text-sm text-cyan-700 font-medium">
                                  {dependant.documents.length} document(s) attached
                                </div>
                              
                              <div className="space-y-2 min-w-0">
                                {dependant.documents.map((doc, index) => (
                                  <div
                                    key={`${dependant.id}-${doc.id}-${index}`}
                                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-md border border-stone-200 bg-stone-50 px-3 py-2 min-w-0"
                                  >
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openDocument(doc);
                                      }}
                                      className="flex items-start gap-2 min-w-0 w-full sm:flex-1 text-sm font-medium text-stone-800 hover:text-cyan-700 transition-colors text-left"
                                    >
                                      {getDocumentIcon(doc, 'w-4 h-4 text-cyan-700 shrink-0 mt-0.5')}
                                      <span className="break-words whitespace-normal min-w-0">
                                        {doc.name}
                                      </span>
                                    </button>

                                    <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          downloadDocument(doc);
                                        }}
                                        className="text-cyan-600 hover:text-cyan-800 text-xs font-medium"
                                        aria-label={`Download ${doc.name}`}
                                        title="Download document"
                                      >
                                        Download
                                      </button>

                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleRemoveSavedDocument('dependent', dependant.id, index);
                                        }}
                                        className="text-red-500 hover:text-red-700 text-xs font-medium"
                                        aria-label={`Remove ${doc.name}`}
                                        title="Remove document"
                                      >
                                        Remove
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {isExpanded && (
                            <div className="mt-3 space-y-2 min-w-0 overflow-hidden border-t border-stone-100 pt-3">
                              {dependant.guardianContact && (
                                <div className="font-sans text-sm text-stone-500 break-words leading-relaxed min-w-0">
                                  <span className="font-medium text-stone-700">Guardian Contact:</span> {dependant.guardianContact}
                                </div>
                              )}

                              {dependant.emergencyContact && (
                                <div className="font-sans text-sm text-stone-500 break-words leading-relaxed min-w-0">
                                  <span className="font-medium text-stone-700">Emergency Contact:</span> {dependant.emergencyContact}
                                </div>
                              )}

                              {dependant.emergencyContactPhone && (
                                <div className="font-sans text-sm text-stone-500 break-words leading-relaxed min-w-0">
                                  <span className="font-medium text-stone-700">Emergency Phone:</span> {dependant.emergencyContactPhone}
                                </div>
                              )}

                              {dependant.medicalConditions && (
                                <div className="font-sans text-sm text-stone-500 break-words leading-relaxed min-w-0">
                                  <span className="font-medium text-stone-700">Medical Conditions:</span> {dependant.medicalConditions}
                                </div>
                              )}

                              {dependant.medications && (
                                <div className="font-sans text-sm text-stone-500 break-words leading-relaxed min-w-0">
                                  <span className="font-medium text-stone-700">Medications:</span> {dependant.medications}
                                </div>
                              )}

                              {dependant.careInstructions && (
                                <div className="font-sans text-sm text-stone-500 whitespace-pre-wrap break-words leading-relaxed min-w-0">
                                  <span className="font-medium text-stone-700">Care Instructions:</span><br/>
                                  {dependant.careInstructions}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2 shrink-0 w-full sm:w-auto">
                        <button
                          onClick={() => toggleExpanded(`adult-${dependant.id}`)}
                          className="text-stone-700 hover:text-stone-900 px-3 py-1 rounded border border-stone-300 hover:border-stone-500 font-sans text-sm font-medium transition-colors"
                        >
                          {isExpanded ? 'Show Less' : 'Show More'}
                        </button>
                        <button
                          onClick={() => handleEdit(dependant, 'dependent')}
                          className="text-cyan-600 hover:text-cyan-800 px-3 py-1 rounded border border-cyan-400 hover:border-cyan-700 font-sans text-sm font-medium transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(dependant.id, 'dependent')}
                          className="text-red-600 hover:text-red-800 px-3 py-1 rounded border border-red-300 hover:border-red-700 font-sans text-sm font-medium transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

          {/* ── Pets ── */}
        <div className="mt-7 min-w-0">
          <div className="flex items-center gap-3 mb-3 min-w-0">
            <h2 className="font-sans text-xl font-medium text-stone-800 break-words">
              Pets
            </h2>
          </div>

          <div className="space-y-3 min-w-0">
            {loading ? (
              <div className="text-center py-12 text-stone-500">
                <div className="inline-block w-6 h-6 border-2 border-cyan-700 border-t-transparent rounded-full animate-spin mb-2" />
                <p className="font-sans text-sm">Loading pets...</p>
              </div>
            ) : data.pets.length === 0 ? (
              <div className="text-center py-12 border border-dashed border-stone-300 rounded-lg">
                <p className="font-sans text-stone-500 text-sm">
                  No pets added yet.
                </p>
              </div>
            ) : (
              data.pets.map((pet) => {
                const isExpanded = !!expandedIds[`pet-${pet.id}`];
                const petTypeLabel =
                  pet.type ? pet.type.charAt(0).toUpperCase() + pet.type.slice(1) : 'Pet';

                const metadataParts = [
                  pet.age || null,
                  pet.veterinarian ? `Vet: ${pet.veterinarian}` : null,
                  pet.preferredCaregiver ? `Caregiver: ${pet.preferredCaregiver}` : null,
                ].filter(Boolean);

                return (
                  <div
                    key={pet.id}
                    className="flex flex-col gap-4 bg-white rounded-lg border border-stone-200 p-4 sm:p-6 min-w-0 overflow-hidden"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start gap-4 min-w-0">
                      <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4 flex-1 min-w-0 overflow-hidden">
                        {getPetIcon(pet.type, 'w-6 h-6 text-cyan-700 flex-shrink-0 mt-0.5')}

                        <div className="flex-1 min-w-0 w-full overflow-hidden">
                          <div className="font-sans text-lg font-medium text-stone-800 break-words">
                            {pet.name}
                          </div>

                          <div className="font-sans text-base text-stone-600 mt-1 break-words">
                            {petTypeLabel}
                          </div>

                          {metadataParts.length > 0 && (
                            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-y-1 sm:gap-x-5 font-sans text-base text-stone-600 mt-1 min-w-0">
                              {metadataParts.map((part, index) => (
                                <span key={index} className="break-words min-w-0">
                                  {part}
                                </span>
                              ))}
                            </div>
                          )}

                          {pet.specialNeeds && (
                            <div className="font-sans text-sm text-stone-500 mt-2 break-words leading-relaxed min-w-0">
                              {pet.specialNeeds}
                            </div>
                          )}

                          {/* Document Section*/}
                          {pet.documents && pet.documents.length > 0 && (
                            <div className="mt-3 space-y-2 min-w-0 overflow-hidden">
                                <div className="text-sm text-cyan-700 font-medium">
                                  {pet.documents.length} document(s) attached
                                </div>
                              
                              <div className="space-y-2 min-w-0">
                                {pet.documents.map((doc, index) => (
                                  <div
                                    key={`${pet.id}-${doc.id}-${index}`}
                                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-md border border-stone-200 bg-stone-50 px-3 py-2 min-w-0"
                                  >
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openDocument(doc);
                                      }}
                                      className="flex items-start gap-2 min-w-0 w-full sm:flex-1 text-sm font-medium text-stone-800 hover:text-cyan-700 transition-colors text-left"
                                    >
                                      {getDocumentIcon(doc, 'w-4 h-4 text-cyan-700 shrink-0 mt-0.5')}
                                      <span className="break-words whitespace-normal min-w-0">
                                        {doc.name}
                                      </span>
                                    </button>

                                    <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          downloadDocument(doc);
                                        }}
                                        className="text-cyan-600 hover:text-cyan-800 text-xs font-medium"
                                        aria-label={`Download ${doc.name}`}
                                        title="Download document"
                                      >
                                        Download
                                      </button>

                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleRemoveSavedDocument('pet', pet.id, index);
                                        }}
                                        className="text-red-500 hover:text-red-700 text-xs font-medium"
                                        aria-label={`Remove ${doc.name}`}
                                        title="Remove document"
                                      >
                                        Remove
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {isExpanded && (
                            <div className="mt-3 space-y-2 min-w-0 overflow-hidden border-t border-stone-100 pt-3">
                              {pet.veterinarianContact && (
                                <div className="font-sans text-sm text-stone-500 break-words leading-relaxed min-w-0">
                                  <span className="font-medium text-stone-700">Vet Contact:</span> {pet.veterinarianContact}
                                </div>
                              )}

                              {pet.caregiverContact && (
                                <div className="font-sans text-sm text-stone-500 break-words leading-relaxed min-w-0">
                                  <span className="font-medium text-stone-700">Caregiver Contact:</span> {pet.caregiverContact}
                                </div>
                              )}

                              {pet.diet && (
                                <div className="font-sans text-sm text-stone-500 break-words leading-relaxed min-w-0">
                                  <span className="font-medium text-stone-700">Diet:</span> {pet.diet}
                                </div>
                              )}

                              {pet.medications && (
                                <div className="font-sans text-sm text-stone-500 break-words leading-relaxed min-w-0">
                                  <span className="font-medium text-stone-700">Medications:</span> {pet.medications}
                                </div>
                              )}

                              {pet.careInstructions && (
                                <div className="font-sans text-sm text-stone-500 whitespace-pre-wrap break-words leading-relaxed min-w-0">
                                  <span className="font-medium text-stone-700">Care Instructions:</span><br/>
                                  {pet.careInstructions}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2 shrink-0 w-full sm:w-auto">
                        <button
                          onClick={() => toggleExpanded(`pet-${pet.id}`)}
                          className="text-stone-700 hover:text-stone-900 px-3 py-1 rounded border border-stone-300 hover:border-stone-500 font-sans text-sm font-medium transition-colors"
                        >
                          {isExpanded ? 'Show Less' : 'Show More'}
                        </button>
                        <button
                          onClick={() => handleEdit(pet, 'pet')}
                          className="text-cyan-600 hover:text-cyan-800 px-3 py-1 rounded border border-cyan-400 hover:border-cyan-700 font-sans text-sm font-medium transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(pet.id, 'pet')}
                          className="text-red-600 hover:text-red-800 px-3 py-1 rounded border border-red-300 hover:border-red-700 font-sans text-sm font-medium transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
          </div>
        </div>
      </ResponsiveContent>

      {/* ── Chooser Modal ── */}
      <Modal
        open={showChooserModal}
        onClose={() => setShowChooserModal(false)}
      >
        <div className="space-y-6 overflow-x-hidden min-w-0">
          <h2 className="text-xl font-semibold text-stone-800 break-words">
            Add Entry
          </h2>

          <div className="min-w-0">
            <h3 className="font-sans text-base font-medium text-stone-800 mb-3 break-words">
              Pets
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 min-w-0">
              {petCategories.map((category) => (
                <button
                  key={category.key}
                  onClick={() => openPetForm(category.key)}
                  className="group w-full bg-white rounded-lg border border-neutral-200 hover:border-cyan-500 transition-colors text-left p-5 min-w-0 overflow-hidden"
                >
                  <div className="flex items-start gap-4 min-w-0">
                    <category.Icon className="w-8 h-8 text-cyan-600 flex-shrink-0" />
                    <div className="min-w-0">
                      <h4 className="font-medium text-stone-800 leading-tight break-words">
                        {category.label}
                      </h4>
                      <p className="text-sm text-stone-600 mt-1 break-words">
                        {category.description}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="min-w-0">
            <h3 className="font-sans text-base font-medium text-stone-800 mb-3 break-words">
              Dependents
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 min-w-0">
              {dependentCategories.map((category) => (
                <button
                  key={category.key}
                  onClick={() => openDependentForm(category.key)}
                  className="group w-full bg-white rounded-lg border border-neutral-200 hover:border-cyan-500 transition-colors text-left p-5 min-w-0 overflow-hidden"
                >
                  <div className="flex items-start gap-4 min-w-0">
                    <category.Icon className="w-8 h-8 text-cyan-600 flex-shrink-0" />
                    <div className="min-w-0">
                      <h4 className="font-medium text-stone-800 leading-tight break-words">
                        {category.label}
                      </h4>
                      <p className="text-sm text-stone-600 mt-1 break-words">
                        {category.description}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </Modal>

      {/* ── Add / Edit Form Modal ── */}
      <Modal
        title={formTitle}
        open={showFormModal}
        onOk={handleSave}
        onCancel={() => {
          setShowFormModal(false);
          resetForm();
        }}
        okText={editingItemId ? 'Update Entry' : 'Add Entry'}
        cancelText="Cancel"
      >
        <div className="space-y-6 overflow-x-hidden min-w-0">
          <div className="min-w-0">
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full min-w-0 px-3 py-2 border border-stone-300 rounded-md"
              placeholder={selectedCategory === 'pet' ? 'Pet name' : 'Dependent name'}
            />
          </div>

          <div className="min-w-0">
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Age
            </label>
            <input
              type="text"
              value={age}
              onChange={(e) => setAge(e.target.value)}
              className="w-full min-w-0 px-3 py-2 border border-stone-300 rounded-md"
              placeholder="Age"
            />
          </div>

          <div className="min-w-0">
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Special Needs
            </label>
            <textarea
              value={specialNeeds}
              onChange={(e) => setSpecialNeeds(e.target.value)}
              className="w-full min-w-0 px-3 py-2 border border-stone-300 rounded-md"
              rows={3}
              placeholder="Special needs"
            />
          </div>

          {selectedCategory === 'dependent' && (
            <>
              <div className="min-w-0">
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Medical Conditions
                </label>
                <textarea
                  value={medicalInfo}
                  onChange={(e) => setMedicalInfo(e.target.value)}
                  className="w-full min-w-0 px-3 py-2 border border-stone-300 rounded-md"
                  rows={3}
                  placeholder="Medical conditions"
                />
              </div>

              <div className="min-w-0">
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Medications
                </label>
                <textarea
                  value={medications}
                  onChange={(e) => setMedications(e.target.value)}
                  className="w-full min-w-0 px-3 py-2 border border-stone-300 rounded-md"
                  rows={3}
                  placeholder="Medications"
                />
              </div>
            </>
          )}

          <div className="min-w-0">
            <label className="block text-sm font-medium text-stone-700 mb-1">
              {selectedCategory === 'pet' ? 'Veterinarian / Primary Contact' : 'Preferred Guardian'}
            </label>
            <input
              type="text"
              value={primaryContact}
              onChange={(e) => setPrimaryContact(e.target.value)}
              className="w-full min-w-0 px-3 py-2 border border-stone-300 rounded-md"
              placeholder={selectedCategory === 'pet' ? 'Veterinarian name' : 'Guardian name'}
            />
          </div>

          <div className="min-w-0">
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Contact Phone
            </label>
            <input
              type="tel"
              value={primaryContactPhone}
              onChange={(e) => setPrimaryContactPhone(e.target.value)}
              className="w-full min-w-0 px-3 py-2 border border-stone-300 rounded-md"
              placeholder="Phone number"
            />
          </div>

          <div className="min-w-0">
            <label className="block text-sm font-medium text-stone-700 mb-1">
              {selectedCategory === 'pet' ? 'Preferred Caregiver / Secondary Contact' : 'Emergency Contact'}
            </label>
            <input
              type="text"
              value={secondaryContact}
              onChange={(e) => setSecondaryContact(e.target.value)}
              className="w-full min-w-0 px-3 py-2 border border-stone-300 rounded-md"
              placeholder={selectedCategory === 'pet' ? 'Caregiver name' : 'Emergency contact name'}
            />
          </div>

          <div className="min-w-0">
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Secondary Contact Phone
            </label>
            <input
              type="tel"
              value={secondaryContactPhone}
              onChange={(e) => setSecondaryContactPhone(e.target.value)}
              className="w-full min-w-0 px-3 py-2 border border-stone-300 rounded-md"
              placeholder="Phone number"
            />
          </div>

          {selectedCategory === 'pet' && (
            <>
              <div className="min-w-0">
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Diet
                </label>
                <textarea
                  value={diet}
                  onChange={(e) => setDiet(e.target.value)}
                  className="w-full min-w-0 px-3 py-2 border border-stone-300 rounded-md"
                  rows={3}
                  placeholder="Diet instructions"
                />
              </div>

              <div className="min-w-0">
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Pet Medications
                </label>
                <textarea
                  value={petMedications}
                  onChange={(e) => setPetMedications(e.target.value)}
                  className="w-full min-w-0 px-3 py-2 border border-stone-300 rounded-md"
                  rows={3}
                  placeholder="Medications"
                />
              </div>
            </>
          )}

          <div className="min-w-0">
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Care Instructions
            </label>
            <textarea
              value={careInstructions}
              onChange={(e) => setCareInstructions(e.target.value)}
              className="w-full min-w-0 px-3 py-2 border border-stone-300 rounded-md"
              rows={4}
              placeholder="Detailed care instructions"
            />
          </div>

          <div className="min-w-0 overflow-hidden">
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Upload Documents
            </label>

            <FileUpload
              onUpload={async (newFiles: File[]) => {
                if (!newFiles.length) return;

                setIsUploadingDocuments(true);

                try {
                  const uploadedDocs = await Promise.all(
                    newFiles.map((file) => documentService.upload(file))
                  );

                  setDocuments((prev) => [...prev, ...uploadedDocs]);
                } catch (error) {
                  console.error('[Frame146] Failed to upload document(s):', error);
                  alert('Failed to upload one or more documents.');
                } finally {
                  setIsUploadingDocuments(false);
                }
              }}
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              multiple
            />

            {isUploadingDocuments && (
              <div className="mt-3 text-sm text-stone-500 break-words">
                Uploading documents...
              </div>
            )}

            {documents.length > 0 && (
              <ul className="mt-4 space-y-2 min-w-0">
                {documents.map((doc, index) => (
                  <li
                    key={`${doc.id}-${index}`}
                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-md border border-stone-200 bg-stone-50 px-3 py-2 min-w-0"
                  >
                    <button
                      type="button"
                      onClick={() => openDocument(doc)}
                      className="flex items-start gap-2 min-w-0 w-full sm:flex-1 text-sm font-medium text-stone-800 hover:text-cyan-700 transition-colors text-left"
                    >
                      {getDocumentIcon(doc, 'w-4 h-4 text-cyan-700 shrink-0 mt-0.5')}
                      <span className="break-words whitespace-normal min-w-0">
                        {doc.name}
                      </span>
                    </button>

                    <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
                      <button
                        type="button"
                        onClick={() => downloadDocument(doc)}
                        className="text-cyan-600 hover:text-cyan-800 text-xs"
                      >
                        Download
                      </button>

                      <button
                        type="button"
                        onClick={() => removeDocumentFromForm(index)}
                        className="text-red-500 hover:text-red-700 text-xs"
                      >
                        Remove
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </Modal>
    </ResponsiveContainer>
  );
};

export default Frame146;